import { useMemo } from 'react';
import { validateDecisionInput } from 'shared/domain/workflow/validation';
import type { IssueWorkflowOutDto, WorkflowStageOutDto } from 'shared/domain/workflow/types';
import { useEntityCollectionInitializer } from 'store/entityCollection';
import { useWorkflowStore, getWorkflowStoreState } from '../store';
import {
  findWorkflowById,
  findStageById,
  findStagesByWorkflowId,
  findStageIndex,
  findStageFromWorkflow,
} from '../model';
import type { UseWorkflowsResult, UseWorkflowDecisionResult } from '../types';

export function useWorkflowsInitializer(): void {
  useEntityCollectionInitializer(useWorkflowStore.use.actions());
}

export function useWorkflows(): UseWorkflowsResult {
  const data = useWorkflowStore.use.data();
  const actions = useWorkflowStore.use.actions();
  const actionStates = useWorkflowStore.use._actionStates();
  const asyncActions = useWorkflowStore.use.asyncActions();

  return {
    workflows: data.items,
    loading: data.loading,
    reload: actions.reload,
    createWorkflow: {
      execute: asyncActions.createWorkflow,
      ...actionStates.createWorkflow,
    },
    updateWorkflow: {
      execute: asyncActions.updateWorkflow,
      ...actionStates.updateWorkflow,
    },
    createStage: {
      execute: asyncActions.createStage,
      ...actionStates.createStage,
    },
    updateStage: {
      execute: asyncActions.updateStage,
      ...actionStates.updateStage,
    },
    deleteStage: {
      execute: asyncActions.deleteStage,
      ...actionStates.deleteStage,
    },
    reorderStage: {
      execute: asyncActions.reorderStage,
      ...actionStates.reorderStage,
    },
  };
}

export function useWorkflow(
  workflowId: string | undefined
): IssueWorkflowOutDto | undefined {
  const data = useWorkflowStore.use.data();
  return useMemo(
    () => (workflowId ? findWorkflowById(data.items, workflowId) : undefined),
    [data.items, workflowId]
  );
}

export function useWorkflowsLoading(): boolean {
  const data = useWorkflowStore.use.data();
  return data.loading;
}

export function useStage(stageId: string | undefined): {
  stage: WorkflowStageOutDto | undefined;
  workflow: IssueWorkflowOutDto | undefined;
} {
  const data = useWorkflowStore.use.data();
  return useMemo(() => {
    if (!stageId) return { stage: undefined, workflow: undefined };
    const result = findStageById(data.items, stageId);
    return result ?? { stage: undefined, workflow: undefined };
  }, [data.items, stageId]);
}

export function useWorkflowStages(
  workflowId: string | undefined
): WorkflowStageOutDto[] {
  const data = useWorkflowStore.use.data();
  return useMemo(
    () => (workflowId ? findStagesByWorkflowId(data.items, workflowId) : []),
    [data.items, workflowId]
  );
}

export function useStageFromWorkflow(
  workflowId: string | undefined,
  stageId: string | undefined
): WorkflowStageOutDto | undefined {
  const data = useWorkflowStore.use.data();
  return useMemo(
    () =>
      workflowId && stageId
        ? findStageFromWorkflow(data.items, workflowId, stageId)
        : undefined,
    [data.items, workflowId, stageId]
  );
}

export function useStageIndex(
  stageId: string | undefined,
  workflowId: string | undefined
): number {
  const data = useWorkflowStore.use.data();
  return useMemo(
    () =>
      stageId && workflowId
        ? findStageIndex(data.items, stageId, workflowId)
        : -1,
    [data.items, stageId, workflowId]
  );
}

export function getStageById(stageId: string):
  | { stage: WorkflowStageOutDto; workflow: IssueWorkflowOutDto }
  | undefined {
  const { data } = getWorkflowStoreState();
  return findStageById(data.items, stageId);
}

export function getWorkflowById(
  workflowId: string
): IssueWorkflowOutDto | undefined {
  const { data } = getWorkflowStoreState();
  return findWorkflowById(data.items, workflowId);
}

export function getStagesByWorkflowId(
  workflowId: string
): WorkflowStageOutDto[] {
  const { data } = getWorkflowStoreState();
  return findStagesByWorkflowId(data.items, workflowId);
}

export function useWorkflowDecision(): UseWorkflowDecisionResult {
  const actionStates = useWorkflowStore.use._actionStates();
  const asyncActions = useWorkflowStore.use.asyncActions();

  return {
    createDecision: {
      execute: asyncActions.createDecision,
      ...actionStates.createDecision,
    },
    validateInput: validateDecisionInput,
  };
}
